import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../context/OrganizationContext';
import useAdsEntitlement from '../hooks/useAdsEntitlement';
import LanguageSelector from '../components/LanguageSelector';
import AdsConfig from '../components/ads/AdsConfig';
import AdsDashboard from '../components/ads/AdsDashboard';
import CreativeLibrary from '../components/ads/CreativeLibrary';
import CreateCampaign from '../components/ads/CreateCampaign';
import { useTranslation } from 'react-i18next';

const TABS = ['dashboard', 'create', 'creatives', 'config'];

export default function AdsStudio() {
  const { context, loading: contextLoading } = useOrganization();
  const { loading: adsLoading, hasAds } = useAdsEntitlement();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [createInitialData, setCreateInitialData] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation('adsStudio');
  const { t: tc } = useTranslation('common');

  if (contextLoading || adsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#666' }}>
        {tc('loading')}
      </div>
    );
  }

  // Upsell view for users without the addon
  if (!hasAds) {
    return (
      <div style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <button onClick={() => navigate('/dashboard')} style={{
            background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', padding: 0, fontSize: '0.9rem'
          }}>
            {tc('backToDashboard')}
          </button>
          <LanguageSelector />
        </div>

        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          backgroundColor: '#fff',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          border: '1px solid #e8e8e8'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📢</div>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem', color: '#1a1a2e' }}>
            {t('upsell.title')}
          </h1>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: '#666', fontWeight: '500' }}>
            {t('upsell.subtitle')}
          </p>
          <p style={{ margin: '0 0 2rem 0', fontSize: '1rem', color: '#888', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
            {t('upsell.description')}
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            maxWidth: '700px',
            margin: '0 auto 2.5rem auto',
            textAlign: 'left'
          }}>
            {['insights', 'aiContent', 'multiPlatform', 'closedLoop'].map((feature) => (
              <div key={feature} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <span style={{ color: '#28a745', fontSize: '1.1rem', flexShrink: 0 }}>&#10003;</span>
                <span style={{ fontSize: '0.9rem', color: '#333' }}>
                  {t(`upsell.features.${feature}`)}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate('/addon')}
            style={{
              padding: '1rem 2.5rem',
              backgroundColor: '#1a73e8',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: '700',
              boxShadow: '0 4px 14px rgba(26, 115, 232, 0.4)',
              transition: 'transform 0.2s'
            }}
          >
            {t('upsell.cta')}
          </button>
        </div>
      </div>
    );
  }

  // Studio view for entitled users
  return (
    <div style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <button onClick={() => navigate('/dashboard')} style={{
          background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', padding: 0, fontSize: '0.9rem'
        }}>
          {tc('backToDashboard')}
        </button>
        <LanguageSelector />
      </div>
      <h1 style={{ margin: '0 0 0.25rem 0' }}>{t('title')}</h1>

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        gap: '0',
        borderBottom: '2px solid #e0e0e0',
        marginBottom: '1.5rem',
        marginTop: '1rem'
      }}>
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.75rem 1.25rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: activeTab === tab ? '600' : '400',
              color: activeTab === tab ? '#1a73e8' : '#666',
              borderBottom: activeTab === tab ? '2px solid #1a73e8' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'color 0.2s'
            }}
          >
            {t(`tabs.${tab}`)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'dashboard' ? (
        <AdsDashboard onCreateAd={(data) => {
          setCreateInitialData(data);
          setActiveTab('create');
        }} />
      ) : activeTab === 'create' ? (
        <CreateCampaign initialData={createInitialData} />
      ) : activeTab === 'creatives' ? (
        <CreativeLibrary />
      ) : activeTab === 'config' ? (
        <AdsConfig />
      ) : null}
    </div>
  );
}
